/**
 * Branded ids and the one UUIDv7 generator (`docs/design/00-contracts.md` §4, §11.1; C-32, C-33).
 *
 * Principle 4: UUIDv7 primary keys everywhere, never sequential integers in URLs. Ids are minted
 * in TypeScript for every application write with the `uuidv7` package (LiosK) because it
 * implements the RFC 9562 *monotonic* counter: two ids minted in the same millisecond in the
 * same process still sort strictly, which is what makes `id` usable as the keyset tiebreaker and
 * keeps B-tree inserts on the right edge. PostgreSQL's `public.uuid_generate_v7()` covers column
 * defaults, seeds and raw SQL. Never mix generators inside one process.
 *
 * Generating in the application means a repository knows the id before the INSERT returns and can
 * put it in the audit entry and the event payload with no extra round trip.
 */
import { uuidv7 } from 'uuidv7';
import { z } from 'zod';

/** A plain string the compiler will not let you mix up with any other kind of id. */
export type Uuid<TBrand extends string = 'Uuid'> = string & { readonly __brand: TBrand };

/** THE id generator. Every application-side id in Tvitvi comes from here. */
export function newId<TBrand extends string = 'Uuid'>(): Uuid<TBrand> {
  return uuidv7() as Uuid<TBrand>;
}

/**
 * Accepts any RFC 4122/9562 uuid, not only v7: our ids are v7, but Supabase's `auth.users.id` is
 * v4 and legitimately crosses the wire (C-33). Strict v7 validation on input would buy nothing
 * and would reject valid data.
 */
export const idSchema = <TBrand extends string>(brand: TBrand) =>
  z
    .uuid()
    .transform((value) => value as Uuid<TBrand>)
    .meta({ id: `${brand}` });

/** 8-4-4-4-12 hex, any RFC version nibble, RFC variant bits. */
export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is Uuid {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/** The version nibble of a uuid (7 for ours, 4 for Supabase's), or null when it is not a uuid. */
export function uuidVersion(value: string): number | null {
  if (!UUID_PATTERN.test(value)) return null;
  return Number.parseInt(value.charAt(14), 16);
}

/**
 * The 48-bit Unix-millisecond timestamp a v7 id carries. For diagnostics and tests only — never
 * for ordering or authorization, where the ids themselves are compared.
 */
export function uuidV7Timestamp(value: string): number | null {
  if (uuidVersion(value) !== 7) return null;
  return Number.parseInt(value.slice(0, 8) + value.slice(9, 13), 16);
}

export type PersonId = Uuid<'PersonId'>;
export type UserId = Uuid<'UserId'>;
export type OrganizationId = Uuid<'OrganizationId'>;
export type MembershipId = Uuid<'MembershipId'>;
export type ContactId = Uuid<'ContactId'>;
export type FestivalId = Uuid<'FestivalId'>;
export type EditionId = Uuid<'EditionId'>;
export type DepartmentId = Uuid<'DepartmentId'>;
export type TaskId = Uuid<'TaskId'>;
export type ThreadId = Uuid<'ThreadId'>;
export type MessageId = Uuid<'MessageId'>;
export type TermsVersionId = Uuid<'TermsVersionId'>;
export type RequestId = Uuid<'RequestId'>;

export const personIdSchema = idSchema('PersonId');
export const userIdSchema = idSchema('UserId');
export const organizationIdSchema = idSchema('OrganizationId');
export const membershipIdSchema = idSchema('MembershipId');
export const contactIdSchema = idSchema('ContactId');
export const festivalIdSchema = idSchema('FestivalId');
export const editionIdSchema = idSchema('EditionId');
export const departmentIdSchema = idSchema('DepartmentId');
export const taskIdSchema = idSchema('TaskId');
export const threadIdSchema = idSchema('ThreadId');
export const messageIdSchema = idSchema('MessageId');
export const termsVersionIdSchema = idSchema('TermsVersionId');
export const requestIdSchema = idSchema('RequestId');
